import { mode, PaymentMode } from '~/hooks/usePaymentMode';
import { BankAccountConnectionStatus, PaymentChannel } from '~/swagger/portal/Api';
import type { BankAccountsFilterValue } from '~/hooks/useBankAccountsFilter';
import type { HydratedBankAccountDto } from '~/swagger/portal/Api';

type OwnerVendorBannerDecisions = {
  showGenericBanner: boolean;
  showPlaidBanner: boolean;
  showStripeBanner: boolean;
};
type StripeOnboardingConnectionExitKind = 'success' | 'denied_or_empty' | 'incomplete';

type BankAccount = HydratedBankAccountDto;

type BankAccountOptionParams = {
  requireConnected?: boolean;
  requireCardCollection?: boolean;
  isCompanyAccount?: boolean;
};

type BankAccountConnectionParams = {
  requireCardCollection?: boolean;
};

type HasAnyConnectedBankAccountParams = {
  requireCardCollection?: boolean;
  requireConnected?: boolean;
};

const hasPlaidBankAccount = (accounts: BankAccount[], mode: PaymentMode) => {
  if (mode === PaymentMode.Pay) {
    return accounts.some(
      (account) =>
        account.connection?.status === BankAccountConnectionStatus.Approved &&
        (account.connection?.supports?.includes(PaymentChannel.Ach) ?? false)
    );
  }
  return accounts.some((account) => account.plaid?.status === BankAccountConnectionStatus.Approved);
};

const hasStripeBankAccount = (accounts: BankAccount[], mode: PaymentMode) => {
  if (mode === PaymentMode.Pay) {
    return accounts.some(
      (account) =>
        account.connection?.status === BankAccountConnectionStatus.Approved &&
        (account.connection?.supports?.includes(PaymentChannel.Card) ?? false)
    );
  }
  return accounts.some((account) => account.stripe?.status === BankAccountConnectionStatus.Approved);
};

export const hasConnectedBankAccount = (accounts: BankAccount[], mode: PaymentMode) => {
  if (mode === PaymentMode.Pay) {
    return accounts.some((account) => account.connection?.status === BankAccountConnectionStatus.Approved);
  }
  return accounts.some(
    (account) =>
      account.plaid?.status === BankAccountConnectionStatus.Approved || account.stripe?.status === BankAccountConnectionStatus.Approved
  );
};

export const filterBankAccounts = (accounts: BankAccount[], searchValue: string, filterValue: BankAccountsFilterValue) => {
  return accounts.filter((account) => {
    const isSearchPassed =
      !searchValue ||
      account.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      account.description?.includes(searchValue.toLowerCase());
    const isPlaidLinkStatusPassed =
      filterValue.plaidLinkStatus === 'All' || !filterValue.plaidLinkStatus ? true : account.plaid?.status === filterValue.plaidLinkStatus;
    const isStripeLinkStatusPassed =
      filterValue.stripeLinkStatus === 'All' || !filterValue.stripeLinkStatus
        ? true
        : account.stripe?.status === filterValue.stripeLinkStatus;
    const isConnectionStatusPassed =
      filterValue.connectionStatus === 'All' || !filterValue.connectionStatus
        ? true
        : account.connection?.status === filterValue.connectionStatus;

    if (mode() === PaymentMode.Pay) {
      return isSearchPassed && isConnectionStatusPassed;
    }

    return isSearchPassed && isPlaidLinkStatusPassed && isStripeLinkStatusPassed;
  });
};

export const isPlaidAchReady = (account: Pick<BankAccount, 'plaid'>) => account.plaid?.status === BankAccountConnectionStatus.Approved;

export const isAchReady = (account: HydratedBankAccountDto | undefined, paymentMode: PaymentMode) => {
  if (!account) {
    return false;
  }
  if (paymentMode === PaymentMode.Pay) {
    return (
      account.connection?.status === BankAccountConnectionStatus.Approved &&
      (account.connection?.supports?.includes(PaymentChannel.Ach) ?? false)
    );
  }
  return account.plaid?.status === BankAccountConnectionStatus.Approved;
};

export const isBankAccountMeetingRequirement = (
  account: BankAccount | undefined,
  mode: PaymentMode,
  params?: BankAccountConnectionParams
) => {
  if (!account) {
    return false;
  }

  if (mode === PaymentMode.Pay) {
    if (params?.requireCardCollection) {
      return (
        account.connection?.status === BankAccountConnectionStatus.Approved &&
        (account.connection?.supports?.includes(PaymentChannel.Card) ?? false)
      );
    }

    return account.connection?.status === BankAccountConnectionStatus.Approved;
  }

  if (params?.requireCardCollection) {
    return account.stripe?.status === BankAccountConnectionStatus.Approved;
  }

  return account.plaid?.status === BankAccountConnectionStatus.Approved || account.stripe?.status === BankAccountConnectionStatus.Approved;
};

export const getBankAccountOptions = (accounts: BankAccount[], params?: BankAccountOptionParams) => {
  return accounts
    .filter((account) => {
      let flag = true;

      if (params?.requireCardCollection) {
        if (mode() === PaymentMode.Pay) {
          flag =
            flag &&
            account.connection?.status === BankAccountConnectionStatus.Approved &&
            (account.connection?.supports?.includes(PaymentChannel.Card) ?? false);
        } else {
          flag = flag && account.stripe?.status === BankAccountConnectionStatus.Approved;
        }
      }

      if (params?.requireConnected) {
        if (mode() === PaymentMode.Pay) {
          flag = flag && account.connection?.status === BankAccountConnectionStatus.Approved;
        } else {
          flag =
            flag &&
            (account.plaid?.status === BankAccountConnectionStatus.Approved ||
              account.stripe?.status === BankAccountConnectionStatus.Approved);
        }
      }

      if (params?.isCompanyAccount !== undefined) {
        flag = flag && account.companyAccount === params.isCompanyAccount;
      }

      return flag;
    })
    .map((account) => ({ label: account.name, value: account.id }));
};

export const hasAnyConnectedBankAccount = (accounts: BankAccount[], params?: HasAnyConnectedBankAccountParams) => {
  if (params?.requireCardCollection) {
    if (mode() === PaymentMode.Pay) {
      return accounts.some(
        (account) =>
          account.connection?.status === BankAccountConnectionStatus.Approved &&
          (account.connection?.supports?.includes(PaymentChannel.Card) ?? false)
      );
    }
    return accounts.some((account) => account.stripe?.status === BankAccountConnectionStatus.Approved);
  }

  if (mode() === PaymentMode.Pay) {
    return accounts.some((account) => account.connection?.status === BankAccountConnectionStatus.Approved);
  }

  return accounts.some(
    (account) =>
      account.plaid?.status === BankAccountConnectionStatus.Approved || account.stripe?.status === BankAccountConnectionStatus.Approved
  );
};

export const isConnectedBankAccountUnmet = (accounts: BankAccount[]) => {
  if (mode() === PaymentMode.Pay) {
    return !accounts.some((account) => account.connection?.status === BankAccountConnectionStatus.Approved);
  }
  return !accounts.some(
    (account) =>
      account.plaid?.status === BankAccountConnectionStatus.Approved || account.stripe?.status === BankAccountConnectionStatus.Approved
  );
};

export const isCardCollectionBankAccountUnmet = (accounts: BankAccount[]) => {
  if (mode() === PaymentMode.Pay) {
    return !accounts.some(
      (account) =>
        account.connection?.status === BankAccountConnectionStatus.Approved &&
        (account.connection?.supports?.includes(PaymentChannel.Card) ?? false)
    );
  }
  return !accounts.some((account) => account.stripe?.status === BankAccountConnectionStatus.Approved);
};

export const getOwnerVendorStyleBannerDecisions = (accounts: BankAccount[], mode: PaymentMode): OwnerVendorBannerDecisions => {
  const connectedMet = hasConnectedBankAccount(accounts, mode);
  const plaidMet = hasPlaidBankAccount(accounts, mode);
  return {
    showGenericBanner: !connectedMet,
    showPlaidBanner: connectedMet && !plaidMet,
    showStripeBanner: false,
  };
};

export const getStripeFollowUpBannerVisible = (accounts: BankAccount[], mode: PaymentMode): boolean => {
  if (mode !== PaymentMode.Portal) {
    return false;
  }
  return hasPlaidBankAccount(accounts, mode) && !hasStripeBankAccount(accounts, mode);
};

const hasOnboardingProviderProgress = (status?: BankAccountConnectionStatus): boolean =>
  !!(status && status !== BankAccountConnectionStatus.Denied && status !== BankAccountConnectionStatus.Incomplete);

const hasPortalOnboardingConnectionProgress = (account: Pick<BankAccount, 'plaid' | 'stripe'>): boolean => {
  const hasPlaid = hasOnboardingProviderProgress(account.plaid?.status);
  const hasStripe = hasOnboardingProviderProgress(account.stripe?.status);
  return hasPlaid || hasStripe;
};

export const getStripeOnboardingConnectionExitKind = (stripeStatus?: BankAccountConnectionStatus): StripeOnboardingConnectionExitKind => {
  if (hasOnboardingProviderProgress(stripeStatus)) {
    return 'success';
  }
  if (!stripeStatus || stripeStatus === BankAccountConnectionStatus.Denied) {
    return 'denied_or_empty';
  }
  return 'incomplete';
};

export const hasOnboardingConnectionProgress = (accounts: BankAccount[], paymentMode: PaymentMode): boolean => {
  const onboardingAccount = accounts.find((account) => account.companyAccount === false);

  if (!onboardingAccount) {
    return false;
  }

  const configured = !!(onboardingAccount.accountNumber && onboardingAccount.routingNumber);

  if (!configured) {
    return false;
  }

  if (paymentMode === PaymentMode.Pay) {
    const connection = onboardingAccount.connection;
    if (!connection) {
      return false;
    }
    const statusOk = hasOnboardingProviderProgress(connection.status);
    const supports = connection.supports ?? [];
    const supportsAchOrCard = supports.includes(PaymentChannel.Ach) || supports.includes(PaymentChannel.Card);
    return statusOk && supportsAchOrCard;
  }

  return hasPortalOnboardingConnectionProgress(onboardingAccount);
};
